import React, { useState, useEffect } from "react";
import { parkTawk } from "../../../utils/tawk";
import {
  Backdrop,
  Fade,
  Modal,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  withStyles,
  Box,
  makeStyles,
  Typography,
  FormHelperText,
  Snackbar,
  CircularProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { bool, func } from "prop-types";
import CloseIcon from "@material-ui/icons/Close";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { showSuccessToast } from "../../../utils/successToast";
import googleIcon from "../../../assets/images/google-icon.svg";
import facebookIcon from "../../../assets/images/facebook-icon.svg";
import usersApi from "../../../apis/usersApi";
import { ALERT_TYPES, MODAL_TYPES } from "../../../utils/constants";
import {
  validateEmail,
  isBrowser,
  isMobile,
} from "../../../utils/common";
import FreeTrialModal from "../FreeTrial";
import clsx from "clsx";
import trackEvent from "../../../utils/appMetrics";
import { detectInAppBrowser, openInBrowserHref, openInBrowserHint } from "../../../utils/inAppBrowser";

const useStyles = makeStyles((theme) => ({
  signInModal: {
    backgroundColor: "#f7f8f9",
    width: 768,
    maxHeight: "92%",
    borderRadius: 16,
    margin: "auto",
    outline: 0,
    overflow: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "100vh",
      borderRadius: 0,
      maxHeight: "100%",
    },
  },
  header: {
    display: "flex",
    width: "100%",
    padding: "9px 0",
    backgroundColor: "#f7f8f9",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    minHeight: 30,
  },
  logo: {
    padding: "14px 0",
    textAlign: "center",
    backgroundColor: "#ffffff",
  },

  form: {
    backgroundColor: "#f7f8f9",
    padding: "24px 18%",
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    [theme.breakpoints.down("sm")]: {
      padding: "24px 8%",
    },
  },
  title: {
    fontSize: 20,
    fontWeight: 400,
    margin: 0,
    textTransform: "uppercase",
    marginBottom: 8,
  },
  subTitle: {
    fontSize: 14,
    fontWeight: 400,
    margin: 0,
    marginBottom: 16,
    color: "#4F4F4F",
  },
  remember: {
    padding: "16px 0",
  },
  newPassword: {
    paddingTop: 16,
    textDecoration: "underline",
    fontSize: 14,
    textAlign: "center",
    cursor: "pointer",
  },
  or: {
    padding: "16px 0",
    fontSize: 14,
    textTransform: "capitalize",
    textAlign: "center",
    position: "relative",
    "&::before": {
      content: '""',
      borderBottom: "1px solid #E0E0E0",
      width: "35%",
      position: "absolute",
      bottom: 25,
      left: 0,
    },
    "&::after": {
      content: '""',
      borderBottom: "1px solid #E0E0E0",
      width: "35%",
      position: "absolute",
      bottom: 25,
      right: 0,
    },
  },
  hr: {
    width: "100%",
    margin: "30px 0",
    border: "1px solid #E0E0E0",
  },
  formControl: {
    marginBottom: 8,
  },
  firstName: {
    flex: 1,
    marginRight: theme.spacing(1),
  },
  lastName: {
    flex: 1,
    marginLeft: theme.spacing(1),
  },
  inputLabel: {
    fontSize: 14,
    color: "#000000",
  },
  sendNow: {
    marginTop: 40,
  },
  iconBack: {
    fontSize: 14,
    marginBottom: theme.spacing(5),
  },
  backText: {
    flexGrow: 1,
    fontSize: 14,
    marginLeft: 20,
  },
  marketingMessage: {
    textAlign: "center", // Center the marketing message
    marginTop: 16,       // Add some spacing from the dropdown
    fontSize: 18,        // Adjust the size as needed
  },
  ownerButton: {
    display: "flex",
    justifyContent: "center", // Center the button
    marginTop: 24,            // Add some spacing between the message and button
  },
}));

const Close = withStyles(() => ({
  root: {
    marginLeft: "auto",
    paddingRight: "18px",
    cursor: "pointer",
  },
}))(Box);

const SignInButton = withStyles(() => ({
  root: {
    color: "#ffffff",
    backgroundColor: "#000000",
    "&:hover": {
      backgroundColor: "#000000",
    },
    height: 50,
    borderRadius: "140px",
  },
}))(Button);

const SignInWithGoogleButton = withStyles(() => ({
  root: {
    height: 50,
    paddingLeft: "10px",
    color: "#000000",
    textTransform: "capitalize",
    fontWeight: 600,
    backgroundColor: "#ffffff",
    borderRadius: "140px",
    "&:hover": {
      backgroundColor: "#ffffff",
    },
  },
}))(Button);

const SignInWithFacebookButton = withStyles(() => ({
  root: {
    height: 50,
    paddingLeft: "10px",
    color: "#000000",
    textTransform: "capitalize",
    fontWeight: 600,
    backgroundColor: "#ffffff",
    borderRadius: "140px",
    "&:hover": {
      backgroundColor: "#ffffff",
    },
  },
}))(Button);

const SignUpButton = withStyles(() => ({
  root: {
    color: "#000000",
    backgroundColor: "#ffffff",
    "&:hover": {
      backgroundColor: "#ffffff",
    },
    height: 50,
    borderRadius: "140px",
  },
}))(Button);

const CustomInput = withStyles(() => ({
  root: {
    "&:focus": {
      backgroundColor: "transparent",
    },
    "& > input": {
      "&:-internal-autofill-selected": {
        backgroundColor: "transparent !important",
        "-webkit-box-shadow": "0 0 0 50px #f7f8f9 inset",
      },
    },
  },
}))(Input);

const IN_APP_NAMES = { facebook: "Facebook", instagram: "Instagram", messenger: "Messenger", tiktok: "TikTok", linkedin: "LinkedIn", snapchat: "Snapchat", twitter: "X" };
const InAppNotice = ({ platform }) => {
  const href = openInBrowserHref(typeof window !== "undefined" ? window.location.href : "/");
  return (
    <div
      data-testid="in-app-notice"
      style={{ background: "#FFF8E6", border: "1px solid #F3D48A", borderRadius: 12, padding: "10px 12px", fontSize: 13, color: "#1a1a1a", lineHeight: 1.45, marginBottom: 10 }}
    >
      Google sign-in can't run inside the {IN_APP_NAMES[platform] || "in-app"} browser. Use your email below, or{" "}
      {href ? (
        <a href={href} style={{ color: "#1a1a1a", fontWeight: 700 }}>open this page in Chrome</a>
      ) : (
        <span>open this page in Safari. <span style={{ color: "#666666" }}>{openInBrowserHint()}</span></span>
      )}
    </div>
  );
};

const ssoButton = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  width: "100%",
  height: 48,
  background: "#ffffff",
  border: "1px solid #E0E0E0",
  borderRadius: 140,
  fontSize: 15,
  fontWeight: 600,
  color: "#111827",
  cursor: "pointer",
  fontFamily: "inherit",
  marginBottom: 12,
};
// Plain-text links inside a sentence (role switch): a button, so it works
// from the keyboard, styled as a link so it does not compete with the CTA.
const linkButton = {
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
  color: "#1a1a1a",
  textDecoration: "underline",
  cursor: "pointer",
};
// Readable size and ink, directly under the button: courts enforce a
// "by continuing you agree" notice when it is conspicuous and adjacent to
// the action, and have refused to when it was tiny grey text.
const consentLine = {
  fontSize: 13,
  color: "#4F4F4F",
  textAlign: "center",
  marginTop: 12,
  lineHeight: 1.5,
};

const SignIn = ({ isOpen, onClose, showCloseIcon, initialType }) => {
  const classes = useStyles();
  const [modalType, setModalType] = useState(initialType);
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [alertType, setAlertType] = useState(ALERT_TYPES.SUCCESS);
  const [alertMessages, setAlertMessages] = useState([]);
  const [signUpErrorMessages, setSignUpErrorMessages] = useState({});
  const [signInErrorMessages, setSignInErrorMessages] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [resetPasswordErrorMessages, setResetPasswordErrorMessages] = useState(
    {}
  );

  // Role defaults to Studio Manager. The modal used to open on a "who are
  // you?" step with two cards, and 117 of the 200 people who opened it since
  // June 2026 left there without picking one; of those who did, 121 of 127
  // picked Studio Manager. Students still get a one-line switch below.
  const [formData, setFormData] = useState({
    role: "owner",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    showPassword: false,
    showConfirmPassword: false,
    rememberMe: false,
    isSubmitted: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  // Honeypot — invisible to humans, auto-filled by dumb bots. Server
  // returns fake success when populated so detection isn't leaked.
  const [honeypot, setHoneypot] = useState("");
  const isOwner = formData.role !== "student";

  // Funnel: fire once whenever the sign-up form is actually shown (modal
  // open + on the SIGN_UP screen). This is the "did they even see the form?"
  // signal that sits between sign_up_cta_clicked and sign_up_form_submitted.
  useEffect(() => {
    if (isOpen && modalType === MODAL_TYPES.SIGN_UP) {
      trackEvent("sign_up_form_viewed", { source: "modal" });
    }
  }, [isOpen, modalType]);

  // The one-line switch under the form. Fires the same event the old role
  // step did, so the funnel keeps one name for "chose a role on purpose".
  const switchRole = (role) => {
    setFormData((prev) => ({ ...prev, role, isSubmitted: false }));
    setSignUpErrorMessages({});
    trackEvent("sign_up_role_selected", { role, via: "switch_link" });
  };

  // SSO from the details step — carry the chosen role through OAuth so the
  // completion page doesn't ask for it again.
  // Known once per mount; the event makes the in-app share measurable.
  const inAppBrowser = detectInAppBrowser();
  useEffect(() => {
    if (inAppBrowser) trackEvent("in_app_browser_detected", { platform: inAppBrowser });
  }, [inAppBrowser]);

  // The chat bubble sits on the bottom of this form (Terms checkbox, submit
  // button) and its greeting opens by itself on top of it. Park it while the
  // modal is up; the cleanup brings it back when the modal closes.
  useEffect(() => {
    if (!isOpen) return undefined;
    return parkTawk();
  }, [isOpen]);

  const onClickSignUpWithGoogle = () => {
    window.location = `/users/auth/google_oauth2?role=${encodeURIComponent(formData.role || "owner")}`;
  };

  const handleClickShowPassword = () => {
    setFormData({ ...formData, showPassword: !formData.showPassword });
  };

  const handleClickShowConfirmPassword = () => {
    setFormData({
      ...formData,
      showConfirmPassword: !formData.showConfirmPassword,
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleChange = (prop) => (event) => {
    setFormData({
      ...formData,
      [prop]: event.target.value,
      isSubmitted: false,
    });
    setSignUpErrorMessages({});
    setSignInErrorMessages({});
  };

  const handleCheckBoxChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.checked });
  };

  const onClickSignInHandler = async () => {
    try {
      setFormData({ ...formData, isSubmitted: true });
      if (formData.email === "" || formData.password === "") return;

      if (!validateEmail(formData.email)) {
        setSignInErrorMessages({ email: "Invalid email" });
        return;
      }

      setIsLoading(true);
      const data = {
        user: {
          email: formData.email,
          password: formData.password,
          remember_me: formData.rememberMe,
        },
      };

      const res = await usersApi.signIn(data);

      localStorage.removeItem("isUserLogout");

      const target = res?.redirect_to || "/";
      window.location.href = target;
    } catch (error) {
      const firstError = Array.isArray(error?.errors) ? error.errors[0] : null;
      if (firstError?.code === "unknown_email") {
        // A visitor with no account is trying to LOG IN — the most common
        // way people get stuck on this modal (session replays show minutes
        // of retries). Say it inline and offer the signup they actually need.
        setSignInErrorMessages({
          email: "No account uses this email yet.",
          unknownEmail: true,
        });
      } else if (error.errors) {
        setShowSnackBar(true);
        setAlertType(ALERT_TYPES.ERROR);
        setAlertMessages(error.errors);
      }
      if (error.error) {
        setShowSnackBar(true);
        setAlertType(ALERT_TYPES.ERROR);
        setAlertMessages([{ detail: error.error }]);
      }
      setIsLoading(false);
    }
  };

  // Jump from a failed sign-in straight into sign-up without losing the
  // email the visitor already typed (a full reset would make them start over).
  const switchToSignUpKeepingEmail = () => {
    trackEvent("sign_up_cta_clicked", { source: "unknown_email_nudge" });
    setSignInErrorMessages({});
    setSignUpErrorMessages({});
    setFormData({ ...formData, role: "owner", password: "", isSubmitted: false });
    setModalType(MODAL_TYPES.SIGN_UP);
  };

  const onClickSignUpHandler = async () => {
    try {
      setFormData({ ...formData, isSubmitted: true });
      const { role, firstName, lastName, email, password } = formData;

      // Four fields, no checkbox, no phone: the phone was optional and the
      // studio's number is asked in onboarding; consent is the line under the
      // button. Every client-side stop is tracked so the next funnel read can
      // say which field people die on instead of guessing.
      const missing = [
        firstName === "" && "first_name",
        lastName === "" && "last_name",
        email === "" && "email",
        password === "" && "password",
      ].filter(Boolean);
      if (missing.length > 0) {
        trackEvent("sign_up_field_error", { field: "required", missing: missing.join(","), role });
        return;
      }

      if (!validateEmail(email)) {
        trackEvent("sign_up_field_error", { field: "email", role });
        setSignUpErrorMessages({ email: "Invalid email" });
        return;
      }

      // Mirror the server's password rules (config/initializers/secure_password.rb)
      // so the user gets an instant, clear message instead of a cryptic 422 —
      // or, worse, a silent failure they read as "the button is broken."
      if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/.test(password)) {
        trackEvent("sign_up_field_error", { field: "password", role });
        setSignUpErrorMessages({
          password:
            "Use at least 6 characters with an uppercase letter, a lowercase letter, and a number.",
        });
        return;
      }

      trackEvent("sign_up_form_submitted", { source: "email", role });
      // Shared event_id between the Pixel and the Conversions API so
      // Meta dedupes the browser + server fire as a single event.
      const eventId = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setIsLoading(true);
      const data = {
        user: {
          role: role,
          first_name: firstName,
          last_name: lastName,
          email: email,
          password: password,
          website: honeypot,
        },
        event_id: eventId,
      };
      const res = await usersApi.signUp(data);
      // Meta Pixel: fire CompleteRegistration so the ad campaign can
      // attribute this signup. A Custom Conversion in Events Manager
      // can filter on content_category for owner-only optimization.
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "CompleteRegistration", { content_category: role }, { eventID: eventId });
      }
      // Server auto-signs the user in and returns where to send them.
      const body = res?.data || res || {};
      const target = body.redirect_to || (role === "owner" ? "/owner/onboarding" : "/");
      localStorage.removeItem("isUserLogout");
      window.location.href = target;
    } catch (error) {
      const fields = error && typeof error === "object" ? error : {};
      trackEvent("sign_up_submit_failed", { role: formData.role, fields: Object.keys(fields).join(",") });
      setSignUpErrorMessages(fields);
      // Never fail silently: if the error isn't tied to a visible field,
      // surface it so the user knows the submit didn't go through — using
      // the server's own words (rate limit, etc.) when it gave any.
      const inlineKeys = ["email", "phone", "password", "first_name", "last_name"];
      const hasInline = Object.keys(fields).some((k) => inlineKeys.includes(k));
      if (!hasInline) {
        const generalMessages = Array.isArray(fields._general)
          ? fields._general.map((detail) => ({ detail }))
          : null;
        setShowSnackBar(true);
        setAlertType(ALERT_TYPES.ERROR);
        setAlertMessages(
          generalMessages && generalMessages.length > 0
            ? generalMessages
            : [{ detail: "We couldn't create your account. Please try again." }]
        );
      }
      setIsLoading(false);
    }
  };

  const onClickSignInWithGoogle = () => {
    window.location = "/users/auth/google_oauth2";
  };

  const onClickSignInWithFacebook = () => {
    window.location = "/users/auth/facebook";
  };

  const resetFormData = () => {
    setFormData({
      role: "owner",
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      rememberMe: false,
      isSubmitted: false,
    });
    setSignUpErrorMessages({});
    setSignInErrorMessages({});
  };

  const onClickUpdateModalType = (type) => {
    resetFormData();
    if (type === MODAL_TYPES.SIGN_UP) {
      trackEvent("sign_up_cta_clicked", { source: "sign_in_modal" });
    }
    setModalType(type);
  };

  const handleCloseSnackBar = () => {
    setShowSnackBar(false);
  };

  const onKeyDownSignIn = async (event) => {
    if (event.keyCode === 13) {
      await onClickSignInHandler();
    }
  };

  const onKeyDownSignUp = async (event) => {
    if (event.keyCode === 13) {
      await onClickSignUpHandler();
    }
  };

  const onClickResetPasswordHandler = async () => {
    try {
      setFormData({ ...formData, isSubmitted: true });

      if (formData.email === "") return;

      if (!validateEmail(formData.email)) {
        setResetPasswordErrorMessages({ email: ["Invalid email"] });
        return;
      }

      setIsLoading(true);
      const data = {
        user: {
          email: formData.email,
        },
      };
      await usersApi.resetPassword(data);
      setIsLoading(false);
      showSuccessToast("Password reset sent · check your email");
      onClose();
    } catch (error) {
      setResetPasswordErrorMessages(error);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={isOpen}
        onClose={onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Fade in={isOpen}>
          <div className={classes.signInModal}>
            {isBrowser() && showCloseIcon && (
              <div className={classes.header}>
                <Close onClick={onClose}>
                  <CloseIcon />
                </Close>
              </div>
            )}
            <Box>
              <div className={classes.logo}>
                <a href="/" style={{ textDecoration: "none" }}>
                  <h1
                    style={{
                      fontSize: 26,
                      fontWeight: 400,
                      lineHeight: "32px",
                      letterSpacing: "0.04em",
                      color: "#000000",
                      textTransform: "uppercase",
                      margin: 0,
                    }}>
                    Swyvel
                  </h1>
                </a>
              </div>
              <form className={classes.form}>
                {modalType === MODAL_TYPES.SIGN_IN && (
                  <>
                    <Typography variant={"h3"} className={classes.title}>
                      Sign in
                    </Typography>
                    <Typography className={classes.subTitle}>
                      Ready to go? Let’s get started!
                    </Typography>
                    <FormGroup row>
                      <FormControl
                        fullWidth
                        error={
                          (formData.isSubmitted && formData.email === "") ||
                          signInErrorMessages.email
                        }
                        className={classes.formControl}
                      >
                        <InputLabel
                          htmlFor="email"
                          className={classes.inputLabel}
                        >
                          Email
                        </InputLabel>
                        <CustomInput
                          id="email"
                          name="email"
                          autoComplete="email"
                          type={"email"}
                          value={formData.email}
                          onChange={handleChange("email")}
                          aria-describedby="email-error-text"
                          onKeyDown={onKeyDownSignIn}
                        />
                        {formData.isSubmitted && formData.email === "" && (
                          <FormHelperText id="email-error-text">
                            Please enter your email.
                          </FormHelperText>
                        )}
                        {formData.isSubmitted &&
                          formData.email !== "" &&
                          signInErrorMessages.email && (
                            <FormHelperText id="email-error-text">
                              {signInErrorMessages.email}
                            </FormHelperText>
                          )}
                      </FormControl>
                      <FormControl
                        fullWidth
                        error={formData.isSubmitted && formData.password === ""}
                      >
                        <InputLabel
                          htmlFor="password"
                          className={classes.inputLabel}
                        >
                          Password
                        </InputLabel>
                        <CustomInput
                          id="password"
                          name="current-password"
                          autoComplete="current-password"
                          type={formData.showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={handleChange("password")}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                              >
                                {formData.showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          }
                          aria-describedby="password-error-text"
                          onKeyDown={onKeyDownSignIn}
                        />
                        {formData.isSubmitted && formData.password === "" && (
                          <FormHelperText id="password-error-text">
                            Please enter your password.
                          </FormHelperText>
                        )}
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.rememberMe}
                            onChange={handleCheckBoxChange}
                            name="rememberMe"
                            color="default"
                          />
                        }
                        label="Remember Me"
                        className={classes.remember}
                      />
                      <FormControl fullWidth>
                        <SignInButton
                          variant="contained"
                          color="primary"
                          onClick={onClickSignInHandler}
                        >
                          {!isLoading ? (
                            "Sign In"
                          ) : (
                            <CircularProgress
                              disableShrink
                              style={{ color: "#fff" }}
                            />
                          )}
                        </SignInButton>
                      </FormControl>
                      {signInErrorMessages.unknownEmail && (
                        <div
                          className="juice-in"
                          style={{
                            width: "100%",
                            background: "#EDF4FF",
                            border: "1px solid #BBD4FF",
                            borderRadius: 12,
                            padding: "12px 16px",
                            marginTop: 14,
                          }}
                        >
                          <Typography
                            style={{ fontSize: 14, color: "#1a1a1a", lineHeight: 1.45 }}
                          >
                            Looks like you're new here, Swyvel accounts start
                            with a free 14-day trial.
                          </Typography>
                          <Typography
                            onClick={switchToSignUpKeepingEmail}
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#007BFF",
                              cursor: "pointer",
                              marginTop: 6,
                            }}
                          >
                            Create your account →
                          </Typography>
                        </div>
                      )}
                      <FormControl fullWidth>
                        <Typography
                          className={classes.newPassword}
                          onClick={() =>
                            onClickUpdateModalType(MODAL_TYPES.RESET_PASSWORD)
                          }
                        >
                          Need new password?
                        </Typography>
                        <span className={classes.or}>Or Sign In</span>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }}>
                          {inAppBrowser ? (
                            <InAppNotice platform={inAppBrowser} />
                          ) : (
                          <SignInWithGoogleButton
                              variant="contained"
                              color="primary"
                              onClick={onClickSignInWithGoogle}
                            >
                              <img
                                src={googleIcon}
                                style={{ marginRight: "10px" }}
                              />{" "}
                              With Google
                            </SignInWithGoogleButton>
                          )}
                          {/* <hr style={{ height: "2px" }} /> */}
                          <SignInWithFacebookButton
                            variant="contained"
                            color="primary"
                            onClick={onClickSignInWithFacebook}
                          >
                            <img
                              src={facebookIcon}
                              style={{ marginRight: "10px" }}
                            />{" "}
                            With Facebook
                          </SignInWithFacebookButton>
                        </div>
                        <hr className={classes.hr} />
                        <SignUpButton
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            onClickUpdateModalType(MODAL_TYPES.SIGN_UP)
                          }
                        >
                          Sign Up
                        </SignUpButton>
                      </FormControl>
                    </FormGroup>
                  </>
                )}
                {modalType === MODAL_TYPES.SIGN_UP && (
                  <>
                    <div key="signup-details" className="juice-in">
                      <Typography variant={"h3"} className={classes.title}>
                        {isOwner ? "Start your free trial" : "Create your student account"}
                      </Typography>
                      <Typography className={classes.subTitle}>
                        {isOwner
                          ? "14 days free · no credit card · about a minute to set up"
                          : "See your schedule, lessons and balance in one place."}
                      </Typography>
                      {inAppBrowser ? (
                        <InAppNotice platform={inAppBrowser} />
                      ) : (
                        <button type="button" style={ssoButton} onClick={onClickSignUpWithGoogle}>
                          <img src={googleIcon} alt="" /> Continue with Google
                        </button>
                      )}
                      <div className={classes.or}>or</div>
                        {/* Honeypot — bots fill this; humans don't see it.
                            readOnly keeps browser autofill from stuffing a
                            URL into it ("website" is a real autofill field)
                            and silently flagging a real person as a bot. */}
                        <input
                          type="text"
                          name="website"
                          readOnly
                          onFocus={(e) => e.target.removeAttribute("readonly")}
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          data-1p-ignore="true"
                          data-lpignore="true"
                          data-form-type="other"
                          value={honeypot}
                          onChange={(e) => setHoneypot(e.target.value)}
                          style={{
                            position: "absolute",
                            left: "-10000px",
                            top: "auto",
                            width: 1,
                            height: 1,
                            overflow: "hidden",
                          }}
                        />
                        <FormControl
                          error={
                            formData.isSubmitted && formData.firstName === ""
                          }
                          className={clsx(
                            classes.formControl,
                            classes.firstName
                          )}
                        >
                          <InputLabel
                            htmlFor="firstName"
                            className={classes.inputLabel}
                          >
                            First Name
                          </InputLabel>
                          <CustomInput
                            id="firstName"
                            name="first_name"
                            autoComplete="given-name"
                            type={"text"}
                            value={formData.firstName}
                            onChange={handleChange("firstName")}
                            onKeyDown={onKeyDownSignUp}
                            aria-describedby="firstName-error-text"
                          />
                          {formData.isSubmitted &&
                            formData.firstName === "" && (
                              <FormHelperText id="firstName-error-text">
                                Please enter your first name.
                              </FormHelperText>
                            )}
                        </FormControl>
                        <FormControl
                          error={
                            formData.isSubmitted && formData.lastName === ""
                          }
                          className={clsx(
                            classes.formControl,
                            classes.lastName
                          )}
                        >
                          <InputLabel
                            htmlFor="lastName"
                            className={classes.inputLabel}
                          >
                            Last Name
                          </InputLabel>
                          <CustomInput
                            id="lastName"
                            name="last_name"
                            autoComplete="family-name"
                            type={"text"}
                            value={formData.lastName}
                            onChange={handleChange("lastName")}
                            onKeyDown={onKeyDownSignUp}
                            aria-describedby="lastName-error-text"
                          />
                          {formData.isSubmitted &&
                            formData.lastName === "" && (
                              <FormHelperText id="lastName-error-text">
                                Please enter your last name.
                              </FormHelperText>
                            )}
                        </FormControl>
                        <FormControl
                          fullWidth
                          error={
                            (formData.isSubmitted && formData.email === "") ||
                            Boolean(signUpErrorMessages.email)
                          }
                          className={classes.formControl}
                        >
                          <InputLabel
                            htmlFor="email"
                            className={classes.inputLabel}
                          >
                            Email
                          </InputLabel>
                          <CustomInput
                            id="email"
                            name="email"
                            autoComplete="email"
                            type={"email"}
                            value={formData.email}
                            onChange={handleChange("email")}
                            onKeyDown={onKeyDownSignUp}
                            aria-describedby="email-error-text"
                          />
                          {formData.isSubmitted && formData.email === "" && (
                            <FormHelperText id="email-error-text">
                              Please enter your email.
                            </FormHelperText>
                          )}
                          {formData.isSubmitted &&
                            formData.email !== "" &&
                            signUpErrorMessages.email && (
                              <FormHelperText id="email-error-text">
                                {signUpErrorMessages.email.replace(
                                  "email",
                                  "Email"
                                )}
                              </FormHelperText>
                            )}
                        </FormControl>
                        <FormControl
                          fullWidth
                          error={
                            (formData.isSubmitted &&
                              formData.password === "") ||
                            Boolean(signUpErrorMessages.password)
                          }
                          className={classes.formControl}
                        >
                          <InputLabel
                            htmlFor="password"
                            className={classes.inputLabel}
                          >
                            Password
                          </InputLabel>
                          <CustomInput
                            id="password"
                            name="new-password"
                            autoComplete="new-password"
                            type={formData.showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={handleChange("password")}
                            onKeyDown={onKeyDownSignUp}
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                  onMouseDown={handleMouseDownPassword}
                                >
                                  {formData.showPassword ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            }
                            aria-describedby="password-error-text"
                          />
                          {formData.isSubmitted &&
                          formData.password === "" ? (
                            <FormHelperText id="password-error-text">
                              Please enter your password.
                            </FormHelperText>
                          ) : signUpErrorMessages.password ? (
                            <FormHelperText id="password-error-text">
                              {signUpErrorMessages.password.replace(
                                "password",
                                "Password"
                              )}
                            </FormHelperText>
                          ) : (
                            <FormHelperText
                              style={{
                                color: "#6B7280",
                                fontSize: "0.72rem",
                                lineHeight: 1.4,
                                marginTop: 4,
                              }}
                            >
                              6+ characters, with an uppercase letter, a
                              lowercase letter, and a number.
                            </FormHelperText>
                          )}
                        </FormControl>
                      <FormControl fullWidth style={{ marginTop: 8 }}>
                        <SignInButton
                          variant="contained"
                          color="primary"
                          onClick={onClickSignUpHandler}
                        >
                          {!isLoading ? (
                            isOwner ? "Start free trial" : "Create account"
                          ) : (
                            <CircularProgress
                              disableShrink
                              style={{ color: "#fff" }}
                            />
                          )}
                        </SignInButton>
                      </FormControl>
                      <Typography style={consentLine}>
                        By continuing you agree to the{" "}
                        <a
                          href="/terms_and_privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "inherit", textDecoration: "underline" }}
                        >
                          Terms of Use &amp; Privacy Policy
                        </a>
                        .
                      </Typography>
                      <Typography style={{ textAlign: "center", fontSize: 14, marginTop: 20, color: "#4F4F4F" }}>
                        Already have an account?{" "}
                        <span
                          style={{ textDecoration: "underline", cursor: "pointer" }}
                          onClick={() => onClickUpdateModalType(MODAL_TYPES.SIGN_IN)}
                        >
                          Sign in
                        </span>
                      </Typography>
                      <Typography style={{ textAlign: "center", fontSize: 13, marginTop: 8, color: "#6b7280" }}>
                        {isOwner ? (
                          <>
                            Taking lessons, not running a studio?{" "}
                            <button type="button" style={linkButton} onClick={() => switchRole("student")}>
                              Sign up as a student
                            </button>
                          </>
                        ) : (
                          <>
                            Run a studio?{" "}
                            <button type="button" style={linkButton} onClick={() => switchRole("owner")}>
                              Sign up as a Studio Manager
                            </button>
                          </>
                        )}
                      </Typography>
                    </div>
                  </>
                )}
                {modalType === MODAL_TYPES.RESET_PASSWORD && (
                  <>
                      <>
                        {" "}
                        <IconButton
                          edge="start"
                          className={classes.iconBack}
                          color="inherit"
                          aria-label="menu"
                          size={"small"}
                          onClick={() =>
                            onClickUpdateModalType(MODAL_TYPES.SIGN_IN)
                          }
                        >
                          <ArrowBackIosIcon style={{ fontSize: "14px" }} />
                          <span className={classes.backText}>Back</span>
                        </IconButton>
                        <Typography variant={"h3"} className={classes.title}>
                          Need new password?
                        </Typography>
                        <Typography className={classes.subTitle}>
                          Please enter your email address. You will receive a
                          link to create a new password via email.
                        </Typography>
                        <FormGroup row>
                          <FormControl
                            fullWidth
                            error={
                              formData.isSubmitted &&
                              (formData.email === "" ||
                                resetPasswordErrorMessages.email)
                            }
                            className={classes.formControl}
                          >
                            <InputLabel
                              htmlFor="email"
                              className={classes.inputLabel}
                            >
                              Email
                            </InputLabel>
                            <CustomInput
                              id="email"
                              type={"email"}
                              value={formData.email}
                              onChange={handleChange("email")}
                              aria-describedby="email-error-text"
                            />
                            {formData.isSubmitted && formData.email === "" && (
                              <FormHelperText id="email-error-text">
                                Please enter your email.
                              </FormHelperText>
                            )}
                            {formData.isSubmitted &&
                              resetPasswordErrorMessages.email && (
                                <FormHelperText error>
                                  {resetPasswordErrorMessages.email}
                                </FormHelperText>
                              )}
                          </FormControl>
                          <FormControl fullWidth>
                            <SignInButton
                              variant="contained"
                              color="primary"
                              onClick={onClickResetPasswordHandler}
                              className={classes.sendNow}
                            >
                              {!isLoading ? (
                                "Send Now"
                              ) : (
                                <CircularProgress
                                  disableShrink
                                  style={{ color: "#fff" }}
                                />
                              )}
                            </SignInButton>
                          </FormControl>
                        </FormGroup>
                      </>
                  </>
                )}
              </form>
            </Box>
          </div>
        </Fade>
      </Modal>
      {alertMessages.length > 0 && showSnackBar && (
        <Snackbar
          open={showSnackBar}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          onClose={handleCloseSnackBar}
        >
          <Alert severity={alertType} onClose={handleCloseSnackBar}>
            {alertMessages[0].detail}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

SignIn.prototype = {
  isOpen: bool,
  showCloseIcon: bool,
  onClose: func,
};

SignIn.defaultProps = {
  showCloseIcon: true,
  initialType: MODAL_TYPES.SIGN_IN,
};

export default SignIn;
